<template>
  <div
    id="funnel_chart"
    class="w-full h-full flex flex-col gap-16 overflow-auto p-4"
  >
    <div id="chart_section" class="flex flex-col items-center gap-5">
      <div
        v-for="(stage, index) in stages"
        :key="`stage_${index}`"
        class="w-full"
      >
        <a-tooltip placement="top" color="var(--tooltip-bg)">
          <template #title>
            <span class="text-[13px] font-normal primary_color">
              {{ stage.count }}
            </span>
          </template>

          <div class="flex flex-col items-center justify-center w-full gap-2">
            <div
              class="flex flex-row items-center justify-between p-6 rounded-lg min-h-[60px] transition-all duration-300 shadow-md hover:scale-[1.02] hover:shadow-lg"
              :style="getStageStyle(stage, index)"
            >
              <div class="flex flex-col gap-0.5 truncate">
                <span class="text-sm font-semibold text-white truncate">
                  {{ stage.name }}
                </span>

                <span class="text-xs text-white opacity-80 truncate">
                  {{ stage.eventName }}
                </span>
              </div>

              <span class="text-xl font-bold text-white">
                {{ formatNumber(stage.count) }}
              </span>
            </div>

            <div
              v-if="index < stages.length - 1"
              class="flex flex-row items-center justify-center gap-2"
            >
              <ThemeImage
                :icon="imageNames.CONVERSION_RATE"
                alt="conversion_rate"
                class="size-4 secondary_color"
              />

              <span class="text-xs font-medium secondary_color">
                {{ getConversionRate(index) }}% conversion ({{
                  getDropOffRate(index)
                }}% drop-off)
              </span>
            </div>
          </div>
        </a-tooltip>
      </div>
    </div>

    <div
      id="summary_section"
      class="flex flex-wrap justify-center gap-3"
      :class="getSummaryGridClass"
    >
      <div
        v-for="section in visibleSummarySections"
        :key="section.id"
        class="summary_card flex flex-row items-center gap-3 p-4 rounded-xl border_8 bg_2 transition-all duration-200 hover:border-white/12 hover:-translate-y-0.5"
      >
        <div class="p-2 rounded-lg" :style="section.iconBg">
          <ThemeImage
            :icon="section.icon"
            :alt="section.id"
            class="size-5"
            :style="section.iconFilter"
          />
        </div>

        <div class="flex flex-col gap-0.5">
          <span class="text-xl font-bold primary_color">{{
            section.value
          }}</span>
          <span class="text-xs secondary_color">{{ section.label }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import {
  funnelStageColors,
  funnelSummarySections,
} from "@/src/constants/analyticConstants.js";
import ThemeImage from "@/src/components/ThemeImage.vue";
import store from "@/src/store/index.js";

const props = defineProps({
  chartData: { type: Object, default: () => ({}) },
  chartConfig: { type: Object, default: null },
});

const availableProperties = computed(() => {
  return store.getters["analyticsConfiguration/availableProperties"] || [];
});

const configuredStages = computed(() => {
  const storeConfig =
    store.getters["analyticsConfiguration/renderedChartConfiguration"] || {};
  if (storeConfig.stages?.length > 0) return storeConfig.stages;
  return props.chartConfig?.stages || [];
});

const stages = computed(() => {
  const rawStages = props.chartData?.stages || [];
  return rawStages
    .map((stage, index) =>
      stage == null
        ? null
        : {
            ...stage,
            eventName: getEventName(index),
          },
    )
    .filter(Boolean);
});

const showTimeToConvert = computed(() => {
  try {
    return props.chartData?.funnel_options?.show_time_to_convert || false;
  } catch (error) {
    console.error(
      "An error occurred while getting show time to convert: ",
      error,
    );
    return false;
  }
});

const totalRecords = computed(() => {
  try {
    if (stages.value.length === 0) return 0;
    return stages.value[0]?.count || 0;
  } catch (error) {
    console.error("An error occurred while calculating total records: ", error);
    return 0;
  }
});

const overallConversion = computed(() => {
  try {
    if (stages.value.length < 2) return 0;
    const firstCount = stages.value[0]?.count || 0;
    const lastCount = stages.value[stages.value.length - 1]?.count || 0;
    if (firstCount === 0) return 0;
    return ((lastCount / firstCount) * 100).toFixed(1);
  } catch (error) {
    console.error(
      "An error occurred while calculating overall conversion: ",
      error,
    );
    return 0;
  }
});

const totalDropOff = computed(() => {
  try {
    return (100 - parseFloat(overallConversion.value)).toFixed(1);
  } catch (error) {
    console.error(
      "An error occurred while calculating total drop off: ",
      error,
    );
    return 0;
  }
});

const avgTimeToConvert = computed(() => {
  try {
    return props.chartData?.avg_time_to_convert || null;
  } catch (error) {
    console.error(
      "An error occurred while getting avg time to convert: ",
      error,
    );
    return null;
  }
});

const visibleSummarySections = computed(() => {
  try {
    const sections = [
      {
        ...funnelSummarySections[0],
        value: formatNumber(totalRecords.value),
      },
      {
        ...funnelSummarySections[1],
        value: `${overallConversion.value}%`,
      },
      {
        ...funnelSummarySections[2],
        value: `${totalDropOff.value}%`,
      },
    ];

    if (showTimeToConvert.value) {
      sections.push({
        ...funnelSummarySections[3],
        value: avgTimeToConvert.value || "—",
      });
    }

    return sections;
  } catch (error) {
    console.error(
      "An error occurred while getting visible summary sections: ",
      error,
    );
    return [];
  }
});

const getSummaryGridClass = computed(() => {
  try {
    const count = visibleSummarySections.value.length;
    if (count === 1) return "summary_single";
    if (count === 2) return "summary_double";
    if (count === 3) return "summary_triple";
    return "summary_quad";
  } catch (error) {
    console.error(
      "An error occurred while getting summary grid class: ",
      error,
    );
    return "summary_quad";
  }
});

const getEventName = (stageIndex) => {
  try {
    const configStage = configuredStages.value?.[stageIndex];
    if (!configStage?.field_id) return "";
    const property = availableProperties.value?.find(
      (prop) => prop?.id === configStage.field_id,
    );
    if (property?.name || property?.originalName) {
      return property.name || property.originalName;
    }
    if (typeof configStage.field_id === "string") {
      return configStage.field_id
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
    }
    return "";
  } catch (error) {
    console.error("An error occurred while getting event name: ", error);
    return "";
  }
};

const getStageStyle = (stage, index) => {
  try {
    const maxCount = stages.value.length > 0 ? stages.value[0]?.count || 1 : 1;
    const currentCount = stage.count || 0;

    let widthPercent = 100;
    if (maxCount > 0) {
      widthPercent = (currentCount / maxCount) * 100;
    }

    const color = funnelStageColors[index % funnelStageColors.length];

    return {
      width: `${widthPercent}%`,
      background: `linear-gradient(135deg, ${color} 0%, ${adjustColor(color, -20)} 100%)`,
    };
  } catch (error) {
    console.error("An error occurred while getting stage style: ", error);
    return {};
  }
};

const getConversionRate = (index) => {
  try {
    if (index >= stages.value.length - 1) return 0;
    const currentCount = stages.value[index]?.count || 0;
    const nextCount = stages.value[index + 1]?.count || 0;
    if (currentCount === 0) return 0;
    return ((nextCount / currentCount) * 100).toFixed(1);
  } catch (error) {
    console.error(
      "An error occurred while calculating conversion rate: ",
      error,
    );
    return 0;
  }
};

const getDropOffRate = (index) => {
  try {
    const conversionRate = parseFloat(getConversionRate(index));
    return (100 - conversionRate).toFixed(1);
  } catch (error) {
    console.error("An error occurred while calculating drop off rate: ", error);
    return 0;
  }
};

const formatNumber = (num) => {
  try {
    if (num === null || num === undefined) return "0";
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + "M";
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + "K";
    }
    return num.toLocaleString();
  } catch (error) {
    console.error("An error occurred while formatting number: ", error);
    return "0";
  }
};

const adjustColor = (hex, amount) => {
  try {
    const num = parseInt(hex.replace("#", ""), 16);
    const r = Math.max(0, Math.min(255, (num >> 16) + amount));
    const g = Math.max(0, Math.min(255, ((num >> 8) & 0x00ff) + amount));
    const b = Math.max(0, Math.min(255, (num & 0x0000ff) + amount));
    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
  } catch (error) {
    console.error("An error occurred while adjusting color: ", error);
    return hex;
  }
};
</script>

<style scoped>
.summary_card {
  flex: 0 0 auto;
}

#summary_section.summary_single .summary_card {
  width: 100%;
  max-width: 400px;
}

#summary_section.summary_double .summary_card {
  width: calc(50% - 6px);
  min-width: 250px;
}

#summary_section.summary_triple .summary_card {
  width: calc(33.333% - 8px);
  min-width: 220px;
}

#summary_section.summary_quad .summary_card {
  width: calc(25% - 9px);
  min-width: 200px;
}

@media (max-width: 1280px) {
  #summary_section.summary_triple .summary_card,
  #summary_section.summary_quad .summary_card {
    width: calc(50% - 6px);
  }
}

@media (max-width: 768px) {
  #summary_section .summary_card {
    width: 100%;
    max-width: none;
  }
}
</style>
